import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, BadgeCheck, Ship, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kicker, useLocalizedDate } from "@/components/site/shared";
import { CATEGORIES, ARTICLES, COMPANY, WHATSAPP_LINK } from "@/data/site";

const WHY = [
  { key: "legal", Icon: BadgeCheck },
  { key: "qc", Icon: ShieldCheck },
  { key: "export", Icon: Ship },
  { key: "response", Icon: MessagesSquare },
];

export default function Home() {
  const { t } = useTranslation();
  const fmtDate = useLocalizedDate();

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[82vh] flex items-center overflow-hidden" data-testid="hero-section">
        <img src="/images/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-3xl">
            <p className="inline-block border border-white/30 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/90">
              {t("hero.badge")}
            </p>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              {t("hero.headline")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/80 max-w-xl leading-relaxed">{t("hero.sub")}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-none font-semibold h-12 px-7" data-testid="hero-cta-products">
                <Link to="/products">
                  {t("hero.ctaProducts")}
                  <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-none font-semibold h-12 px-7 border-white/60 bg-transparent text-white hover:bg-white hover:text-black" data-testid="hero-cta-quote">
                <Link to="/contact">{t("hero.ctaQuote")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b bg-background" data-testid="trust-bar">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
          {[
            { label: t("trust.entity"), value: COMPANY.legalEntity },
            { label: t("trust.nib"), value: COMPANY.nib },
            { label: t("trust.npwp"), value: COMPANY.npwp },
            { label: t("trust.response"), value: t("trust.responseValue") },
          ].map((item, i) => (
            <div key={i} className="py-6 px-4 border-s first:border-s-0 border-border">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{item.label}</p>
              <p className="mt-1 font-heading text-sm font-bold break-words">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 lg:py-28" data-testid="overview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">
          <div>
            <Kicker>{t("overview.kicker")}</Kicker>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{t("overview.title")}</h2>
          </div>
          <div>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{t("overview.text")}</p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all" data-testid="overview-learn-more">
              {t("overview.learnMore")}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 lg:py-28 bg-secondary/50 border-y" data-testid="categories-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Kicker>{t("categories.kicker")}</Kicker>
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{t("categories.title")}</h2>
            <p className="text-muted-foreground max-w-md">{t("categories.subtitle")}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {CATEGORIES.map((cat) => (
              <Link to={`/products?cat=${cat.id}`} key={cat.id} className="group border bg-card overflow-hidden" data-testid={`category-card-${cat.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={cat.image} alt={t(`categories.${cat.id}.title`)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold">{t(`categories.${cat.id}.title`)}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`categories.${cat.id}.desc`)}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    {t("categories.view")}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why ANSEN */}
      <section className="py-20 lg:py-28" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Kicker>{t("why.kicker")}</Kicker>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">{t("why.title")}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border">
            {WHY.map(({ key, Icon }) => (
              <div key={key} className="bg-background p-7" data-testid={`why-card-${key}`}>
                <Icon className="h-7 w-7 text-primary" />
                <h3 className="mt-5 font-heading text-lg font-bold">{t(`why.${key}.title`)}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`why.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insights preview */}
      <section className="py-20 lg:py-28 bg-secondary/50 border-y" data-testid="insights-preview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Kicker>{t("insightsPreview.kicker")}</Kicker>
          <div className="mt-4 flex items-end justify-between gap-4">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{t("insightsPreview.title")}</h2>
            <Link to="/insights" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-primary" data-testid="insights-view-all">
              {t("insightsPreview.viewAll")}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {ARTICLES.map((a) => (
              <Link to={`/insights/${a.id}`} key={a.id} className="group border bg-card overflow-hidden" data-testid={`home-article-card-${a.id}`}>
                <div className="aspect-video overflow-hidden">
                  <img src={a.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="bg-primary text-primary-foreground px-2 py-0.5 font-semibold uppercase tracking-wider">{t(`insights.tags.${a.tag}`)}</span>
                    <span>{fmtDate(a.date)}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-lg font-bold leading-snug group-hover:text-primary transition-colors">{t(`articles.${a.id}.title`)}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-primary text-primary-foreground" data-testid="cta-banner">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{t("cta.title")}</h2>
            <p className="mt-3 text-white/80 max-w-xl">{t("cta.text")}</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button asChild size="lg" className="rounded-none font-semibold h-12 px-7 bg-white text-black hover:bg-neutral-200" data-testid="cta-banner-quote">
              <Link to="/contact">{t("cta.quote")}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-none font-semibold h-12 px-7 border-white/60 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="cta-banner-whatsapp">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">{t("cta.whatsapp")}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
